import React from 'react';
import {
    IonItem,
    IonLabel,
    IonInput,
    IonButton,
    IonText,
    useIonToast,
    IonSpinner
} from '@ionic/react';
import { useAuth } from '../contexts/AuthContext';
import { PasswordRequirements } from './PasswordRequirements';
import { PasswordMatchIndicator } from './PasswordMatchIndicator';

export const ChangePasswordForm: React.FC = () => {
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmNewPassword, setConfirmNewPassword] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);
    const [present] = useIonToast();
    const { changePassword } = useAuth();
    const [isLoading, setIsLoading] = React.useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Basic validation
        if (!currentPassword || !newPassword || !confirmNewPassword) {
            setError('All fields are required');
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setError('New passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            await changePassword(currentPassword, newPassword);
            present({
                message: 'Password changed successfully!',
                duration: 3000,
                position: 'bottom',
                color: 'success'
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to change password';
            setError(message);
            present({
                message,
                duration: 3000,
                position: 'bottom',
                color: 'danger'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <IonItem>
                <IonLabel position="floating">Current Password</IonLabel>
                <IonInput
                    type="password"
                    value={currentPassword}
                    onInput={e => setCurrentPassword((e.target as HTMLInputElement).value)}
                    required
                    disabled={isLoading}
                />
            </IonItem>

            <IonItem>
                <IonLabel position="floating">New Password</IonLabel>
                <IonInput
                    type="password"
                    value={newPassword}
                    onInput={e => setNewPassword((e.target as HTMLInputElement).value)}
                    required
                    disabled={isLoading}
                />
            </IonItem>

            <PasswordRequirements password={newPassword} />

            <IonItem>
                <IonLabel position="floating">Confirm New Password</IonLabel>
                <IonInput
                    type="password"
                    value={confirmNewPassword}
                    onInput={e => setConfirmNewPassword((e.target as HTMLInputElement).value)}
                    required
                    disabled={isLoading}
                />
            </IonItem>

            <PasswordMatchIndicator password={newPassword} confirmPassword={confirmNewPassword} />

            {error && (
                <IonText color="danger" className="ion-padding">
                    <p>{error}</p>
                </IonText>
            )}

            <div className="ion-padding">
                <IonButton
                    expand="block"
                    type="submit"
                    disabled={isLoading}
                >
                    {isLoading ? <IonSpinner name="dots" /> : 'Change Password'}
                </IonButton>
            </div>
        </form>
    );
}; 